import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Lock, CheckCircle } from "lucide-react";

export default function Verify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [isVerified, setIsVerified] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const handleEmailVerification = async () => {
      // Normalize auth params from both query (?foo) and hash (#foo)
      const queryParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(
        window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash
      );

      const getParam = (key: string) => queryParams.get(key) ?? hashParams.get(key);

      const accessToken = getParam('access_token');
      const refreshToken = getParam('refresh_token'); 
      const tokenHash = getParam('token_hash');
      const type = getParam('type');
      const code = getParam('code');
      
      if (code) {
        // Newer code flow (PKCE / verify email, recovery, invite)
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          console.error('Failed to exchange code:', error);
          toast({
            title: 'Invalid or Expired Link',
            description: `The verification link is invalid or has expired. ${error.message || 'Please request a new one.'}`,
            variant: 'destructive',
          });
        } else {
          setIsVerified(true);
          setUserEmail(data.user?.email || "");
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Check if password setup is required
          const { data: userData } = await supabase
            .from('app_users')
            .select('require_password_reset')
            .eq('id', data.user.id)
            .single();
          
          setTimeout(() => {
            setIsRedirecting(true);
            if (userData?.require_password_reset) {
              navigate('/setup-password');
            } else {
              navigate('/driver');
            }
          }, 2000);
        }
      } else if (accessToken && refreshToken) {
        // Standard token flow (action links from admin.generateLink)
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) {
          console.error('Failed to set session:', error);
          toast({
            title: 'Authentication Error',
            description: `Failed to authenticate. ${error.message || 'Please try clicking the link again.'}`,
            variant: 'destructive',
          });
        } else {
          setIsVerified(true);
          setUserEmail(data.user?.email || "");
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Check if password setup is required
          const { data: userData } = await supabase
            .from('app_users')
            .select('require_password_reset')
            .eq('id', data.user.id)
            .single();
          
          setTimeout(() => {
            setIsRedirecting(true);
            if (userData?.require_password_reset) {
              navigate('/setup-password');
            } else {
              navigate('/driver');
            }
          }, 2000);
        }
      } else if (tokenHash && type) {
        // Handle token_hash flow (newer Supabase auth)
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type as any,
        });
        if (error) {
          console.error('Failed to verify token:', error);
          toast({
            title: 'Invalid or Expired Link', 
            description: `The verification link is invalid or has expired. ${error.message || 'Please request a new one.'}`,
            variant: 'destructive',
          });
        } else {
          setIsVerified(true);
          setUserEmail(data.user?.email || "");
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Check if password setup is required
          const { data: userData } = await supabase
            .from('app_users')
            .select('require_password_reset')
            .eq('id', data.user.id)
            .single();
          
          setTimeout(() => {
            setIsRedirecting(true);
            if (userData?.require_password_reset) {
              navigate('/setup-password');
            } else {
              navigate('/driver');
            }
          }, 2000);
        }
      } else {
        // Check if user is already authenticated (direct navigation)
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setIsVerified(true);
          setUserEmail(session.user.email || "");
          
          // Check if password setup is required
          const { data: userData } = await supabase
            .from('app_users')
            .select('require_password_reset')
            .eq('id', session.user.id)
            .single();
          
          setTimeout(() => {
            setIsRedirecting(true);
            if (userData?.require_password_reset) {
              navigate('/setup-password');
            } else {
              navigate('/driver');
            }
          }, 2000);
        } else {
          toast({
            title: 'Invalid Link',
            description: 'This verification link appears to be invalid.',
            variant: 'destructive',
          });
        }
      }
    };

    handleEmailVerification();
  }, [searchParams, toast]);


  if (!isVerified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <main className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle className="text-2xl">Verification Required</CardTitle>
              <p className="text-sm text-muted-foreground">
                Please check your email and click the verification link to continue.
              </p>
            </CardHeader>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <main className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Email Verified</CardTitle>
            <p className="text-sm text-muted-foreground">
              Welcome to Lysara! Your email {userEmail} has been verified successfully.
            </p>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              {isRedirecting ? "Redirecting to password setup..." : "You will be redirected to set up your password in a moment."}
            </p>
            {!isRedirecting && (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}